import { createSelector } from 'reselect';

import { selectOrganization } from 'entities/user/selectors';
import type { State } from 'reducers';

import type Cause from './model';

const sortFunction = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name);
const selectCauseState = (state: State) => state.get('entities').cause;

export const selectCauses = createSelector(
  selectCauseState,
  selectOrganization,
  (_state, includeExcluded = false) => includeExcluded,
  (causes, organization, includeExcluded) =>
    causes
      .sort(sortFunction)
      .filter(
        (cause) =>
          includeExcluded || !organization || !organization.excludeCauses.includes(cause.id)
      )
);

export const selectCauseById = createSelector(
  selectCauseState,
  (_causes, causeId) => causeId,
  (causes, causeId) => causes.get(causeId)
);

export const selectPillars = createSelector(selectCauses, (causes) =>
  causes.filter((cause) => cause?.type === 'Pillar').sort(sortFunction)
);

export const selectERGs = createSelector(selectCauses, (causes) =>
  causes.filter((cause) => cause?.type === 'ERG').sort(sortFunction)
);

export const selectERGsandPillars = createSelector(selectCauses, (causes) =>
  causes.filter((cause) => cause?.type === 'ERG' || cause?.type === 'Pillar').sort(sortFunction)
);

export const selectCategories = createSelector(selectCauses, (causes) =>
  causes.filter((cause) => cause?.type === 'Category').sort(sortFunction)
);

export const selectSDGs = createSelector(selectCauses, (causes) =>
  causes.filter((cause) => cause?.type === 'SDG').sort((a, b) => a.get('number') - b.get('number'))
);

const causeToChild = (cause: Cause) => ({
  key: cause.get('id'),
  value: cause.get('id'),
  title: cause.label(),
});

export const selectNestedCauses = createSelector(
  [selectPillars, selectERGs, selectCategories, selectOrganization],
  (pillars, ERGs, categories, organization) =>
    [
      pillars.size > 0 && {
        title: `${organization?.isCompany() ? organization?.name : ''} Cause Areas`.trim(),
        value: 'pillars',
        children: pillars.toArray().map(causeToChild),
      },
      ERGs.size > 0 && {
        title: `${organization?.isCompany() ? organization?.name : ''} ERGs`.trim(),
        value: 'ERGs',
        children: ERGs.toArray().map(causeToChild),
      },
      categories.size > 0 && {
        title: 'Categories',
        value: 'categories',
        children: categories.toArray().map(causeToChild),
      },
    ].filter(Boolean)
);
